"use client";

import type { HeroPreset } from "@marketing/home/components/VideoGeneratorHero";
import { EffectWorkbench } from "@saas/effect-workbench/components/EffectWorkbench";
import { useTranslations } from "next-intl";
import {
	AI_DANCE_VIDEO_MOTION_ASPECT_RATIO,
	AI_DANCE_VIDEO_MOTION_LITE_SECONDS,
	AI_DANCE_VIDEO_MOTION_SECONDS,
	AI_DANCE_VIDEO_NOTE_PREFIX,
	AI_DANCE_VIDEO_PAGE_PATH,
	AI_DANCE_VIDEO_PENDING_STATE_KEY,
	AI_DANCE_VIDEO_PRESET,
	AI_DANCE_VIDEO_QUALITIES,
	AI_DANCE_VIDEO_TEMPLATES,
	AI_DANCE_VIDEO_USER_VIDEO_MAX_SECONDS,
	AI_DANCE_VIDEO_USER_VIDEO_QUALITIES,
	type AiDanceVideoDanceId,
	aiDanceMotionPath,
	buildAiDanceVideoPrompt,
} from "../config/ai-dance-video-preset";
import { aiDanceVideoShowcaseClips } from "../config/ai-dance-video-showcase";

/** 参考片地址：和后端校验用同一个变量拼，后端只收这个域下的参考视频 */
const R2_PUBLIC_URL =
	process.env.NEXT_PUBLIC_R2_PUBLIC_URL ?? "https://images.imagesart.ai";

const referenceVideos = (id: AiDanceVideoDanceId) => ({
	referenceVideo: {
		url: `${R2_PUBLIC_URL}/${aiDanceMotionPath(id)}`,
		durationSec: AI_DANCE_VIDEO_MOTION_SECONDS,
		aspectRatio: AI_DANCE_VIDEO_MOTION_ASPECT_RATIO,
	},
	referenceVideoByQuality: {
		lite: {
			url: `${R2_PUBLIC_URL}/${aiDanceMotionPath(id, true)}`,
			durationSec: AI_DANCE_VIDEO_MOTION_LITE_SECONDS,
			aspectRatio: AI_DANCE_VIDEO_MOTION_ASPECT_RATIO,
		},
	},
});

/**
 * 把 aiDanceVideo 的预设 + 文案拼成 HeroPreset，交给 Studio 的特效工作台（同 AiTongueKissWorkbench）。
 * 舞蹈 chip 每支挂自己的参考片；最后一个 Your own dance 出视频上传格（userVideo）。右栏放当前这支舞的样片，
 * Your own dance 没有样片，放清单第一条
 */
export function AiDanceVideoWorkbench() {
	const t = useTranslations("aiDanceVideo.hero");
	const tShowcase = useTranslations("aiDanceVideo.showcase");
	const preset: HeroPreset = {
		mode: AI_DANCE_VIDEO_PRESET.mode,
		qualities: AI_DANCE_VIDEO_QUALITIES.map((quality) => ({
			...quality,
			label: t(`qualities.${quality.id}.label`),
			hint: t(`qualities.${quality.id}.hint`),
			// Lite 只照抄前 2 秒，升档才是整支 8 秒
			...(quality.id === "lite"
				? {
						note: t("qualities.lite.note"),
						upgrade: {
							text: t("qualities.lite.upgradeText"),
							cta: t("qualities.lite.upgradeCta"),
							targetId: "standard",
						},
					}
				: {}),
		})),
		defaultQualityId: AI_DANCE_VIDEO_PRESET.defaultQualityId,
		aspectRatioOptions: AI_DANCE_VIDEO_PRESET.aspectRatioOptions,
		defaultAspectRatio: AI_DANCE_VIDEO_PRESET.defaultAspectRatio,
		durationOptions: AI_DANCE_VIDEO_PRESET.durationOptions,
		defaultDuration: AI_DANCE_VIDEO_PRESET.defaultDuration,
		pendingStateKey: AI_DANCE_VIDEO_PENDING_STATE_KEY,
		slotLabels: [t("slots.left")],
		templates: AI_DANCE_VIDEO_TEMPLATES.map((template) => ({
			id: template.id,
			label: t(`templates.${template.id}`),
			prompt: template.prompt,
			promptByQuality: template.promptByQuality,
			...(template.id === "own"
				? {
						userVideo: {
							maxSeconds: AI_DANCE_VIDEO_USER_VIDEO_MAX_SECONDS,
							qualityIds: AI_DANCE_VIDEO_USER_VIDEO_QUALITIES,
						},
					}
				: referenceVideos(template.id)),
		})),
		buildPrompt: buildAiDanceVideoPrompt,
		notePrefixLength: AI_DANCE_VIDEO_NOTE_PREFIX.length,
		copy: {
			title: t("title"),
			subtitle: t("subtitle"),
			badges: [
				t("badges.free"),
				t("badges.photo"),
				t("badges.music"),
				t("badges.noCard"),
			],
			freeCredits: t("freeCredits"),
			templatesLabel: t("templatesLabel"),
			qualityLabel: t("qualityLabel"),
			ratioLabel: t("ratioLabel"),
			durationLabel: t("durationLabel"),
			noteLabel: t("noteLabel"),
			notePlaceholder: t("notePlaceholder"),
			moreLinks: t("moreLinks"),
			userVideoLabel: t("userVideo.label"),
			userVideoHint: t("userVideo.hint"),
			trust: [t("trust.aiContent"), t("trust.consent"), t("trust.music")],
		},
	};
	return (
		<EffectWorkbench
			preset={preset}
			slotImageFit="contain"
			currentHref={AI_DANCE_VIDEO_PAGE_PATH}
			// 切哪支舞右栏就放哪支的样片，让人一眼看到这支舞跳起来什么样（11/01 老公提）
			followTemplateExamples
			examples={aiDanceVideoShowcaseClips.map((clip) => ({
				id: clip.id,
				title: tShowcase(`clips.${clip.id}.title`),
				videoUrl: clip.videoUrl,
				poster: clip.poster,
				orientation: clip.orientation,
				inputImages: clip.inputImages,
				templateId: clip.template,
			}))}
			exampleLabels={{
				title: tShowcase("title"),
				use: tShowcase("useImages"),
				mute: tShowcase("mute"),
				unmute: tShowcase("unmute"),
			}}
		/>
	);
}
